import {
  REJECTION_REASONS,
  REJECTION_REASON_LABELS_PL,
  type RejectionReasonCode,
} from "@anisync/types";
import { ThumbsDownIcon } from "lucide-react";
import { useState } from "react";
import { ApiError, type RejectResult, rejectMedia } from "../lib/api";
import { useSession } from "../lib/use-session";
import { cn } from "../lib/utils";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";

/**
 * „Zła wersja".
 *
 * Powstał z przeglądu z 12.10.2026. Źródła opisują kopie nierzetelnie — kopia
 * z francuskimi napisami była podpisana „Shinden", a pod linkiem odcinka jednej
 * serii leżał odcinek innej — i nic w ofercie takich kopii nie odróżnia. Kaskada
 * jest deterministyczna, więc bez tej pamięci wybierała je przy każdym „Odtwórz".
 * Jedynym źródłem tej wiedzy jest widz, który materiał zobaczył.
 *
 * Okno mówi wprost, że odrzucenie działa dla WSZYSTKICH widzów i jak je cofnąć:
 * to decyzja o skutkach dla całej instancji, a nie osobista preferencja.
 *
 * Powód jest obowiązkowy, bo trafia do dziennika i do okna wersji, gdzie ktoś
 * inny zdecyduje, czy kopię przywrócić. „Zła" bez powodu nie da się zweryfikować.
 */
export function WrongVersionButton({
  mediaFileId,
  size = "sm",
  iconOnly = false,
  onDone,
}: {
  mediaFileId: string;
  size?: "sm" | "icon";
  /** Wersja do miejsc, gdzie obok stoją inne przyciski z samą ikoną. */
  iconOnly?: boolean;
  /** Wołane po zapisaniu odrzucenia — ekran decyduje, co pokazać dalej. */
  onDone: (result: RejectResult) => void;
}) {
  const { session } = useSession();
  const [open, setOpen] = useState(false);

  // Trasa wymaga konta: odrzucenie zmienia wybór kopii dla wszystkich, więc gość
  // z zaproszenia nie może go wykonać — i nie ma po co widzieć przycisku.
  if (session?.authenticated !== true) return null;

  return (
    <>
      <Button
        variant="ghost"
        size={iconOnly ? "icon" : size}
        onClick={() => setOpen(true)}
        aria-label="Zła wersja — pobierz inną kopię"
        title="Zła wersja (inny język, inny odcinek, lektor)"
        className={cn(iconOnly && "text-muted-foreground hover:text-warning")}
      >
        <ThumbsDownIcon />
        {!iconOnly && "Zła wersja"}
      </Button>
      <WrongVersionDialog
        mediaFileId={mediaFileId}
        open={open}
        onOpenChange={setOpen}
        onDone={onDone}
      />
    </>
  );
}

/**
 * Samo okno „Zła wersja" — sterowane z zewnątrz.
 *
 * Wydzielone dla listy odcinków, gdzie wejściem jest pozycja menu wiersza, a nie
 * osobny przycisk: dwadzieścia cztery przyciski z kciukiem w dół na jednej stronie
 * zamieniały listę w ścianę ikon.
 */
export function WrongVersionDialog({
  mediaFileId,
  open,
  onOpenChange,
  onDone,
}: {
  mediaFileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: (result: RejectResult) => void;
}) {
  const [reason, setReason] = useState<RejectionReasonCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (reason === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await rejectMedia(mediaFileId, reason);
      onOpenChange(false);
      setReason(null);
      onDone(result);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Nie udało się odrzucić tej wersji");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
        if (!next) setError(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogTitle>Co jest nie tak z tą wersją?</AlertDialogTitle>
        <AlertDialogDescription>
          Ta kopia zostanie zapamiętana jako zła <strong>dla wszystkich</strong>, plik zniknie, a
          serwer pobierze następną kopię tego odcinka. Pomyłkę cofniesz w oknie wybierania wersji.
        </AlertDialogDescription>

        {/*
            Powody jako przyciski, nie lista rozwijana: cztery opcje mieszczą się
            naraz, a pilotem wybór z listy to trzy naciśnięcia więcej. Stan
            zaznaczenia niesie `aria-pressed`.
          */}
        <div className="grid gap-2 sm:grid-cols-2">
          {REJECTION_REASONS.map((code) => (
            <Button
              key={code}
              variant={reason === code ? "secondary" : "outline"}
              aria-pressed={reason === code}
              onClick={() => setReason(code)}
              className="justify-start"
            >
              {REJECTION_REASON_LABELS_PL[code]}
            </Button>
          ))}
        </div>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Anuluj</AlertDialogCancel>
          <Button onClick={() => void submit()} disabled={reason === null || busy}>
            {busy ? "Odrzucam…" : "Odrzuć i pobierz inną"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
